import type { FC } from 'react';

import { Image } from '@/core/Image/Image';
import { isB2C, isFrancePlatform } from '@/core/settings/utils';
import { DeliveryLabel } from '@/core/tamagoshi/ProductCard/components/DeliveryLabel/DeliveryLabel';
import { Typography } from '@/core/tamagoshiv2/components/Typography/Typography';
import { getDelivery } from '@/domains/productDiscovery/ProductCard/utils/getDelivery';
import { AddToCartErrorLabel } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/AddToCartErrorLabel';
import { type Error } from '@/domains/shoppingCart/AddToCart/AddToCart.interfaces';
import { ProductCoupon } from '@/loyalty/components/Coupon/ProductCoupon';
import {
  fastDelivery,
  fastDeliveryByMM,
  freeDelivery,
  freeDeliveryByMM,
  freeFastDeliveryByMM,
} from '@/productDiscovery/ProductCard/translations';
import { useDeliveryMeta } from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import { ProductPrice } from '@/productDiscovery/ProductPage/common/modules/Price/ProductPrice';
import ProLogoIcon from '@/productDiscovery/ProductPage/components/icons/ProLogoIcon';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/productDiscovery/ProductPage/hooks/useQuantity';

import {
  fastMMDeliveryWithReturn,
  freeMMDeliveryWithReturn,
  quantity as quantityTranslation,
} from './translations';

import styles from './mainProduct.module.scss';

export const MainProduct: FC<{ error?: Error }> = ({ error }) => {
  const { delivery, media, title, productId, price, isProProduct } =
    useDetail<'b2c'>();

  const meta = useDeliveryMeta();

  const quantity = useQuantity();

  const deliveryLabel = getDelivery({
    hasFreeDelivery: meta.hasFreeDelivery,
    isMmf: delivery?.fulfillment || false,
  });

  const deliveryTranslations = {
    freeDelivery,
    freeFastDeliveryByMM,
    fastDelivery,
    freeDeliveryByMM: isB2C() ? freeMMDeliveryWithReturn : freeDeliveryByMM,
    fastDeliveryByMM:
      isB2C() && isFrancePlatform()
        ? fastMMDeliveryWithReturn
        : fastDeliveryByMM,
  };

  return (
    <div className={styles.productGrid}>
      <Image
        className={styles.thumbnail}
        alt="product-summary-thumbnail"
        src={media[0].thumbnailUrl}
        height="80"
        width="80"
      />
      <Typography type="semiBold" variant="body2" className={styles.title}>
        {title}
      </Typography>
      {error && (
        <AddToCartErrorLabel error={error} className={styles.errorLabel} />
      )}
      {deliveryLabel && !error && (
        <DeliveryLabel
          translations={deliveryTranslations}
          deliveryLabel={deliveryLabel}
          className={styles.delivery}
        />
      )}
      <Typography type="regular" variant="small" className={styles.quantity}>
        {quantityTranslation.format({ number: quantity })}
      </Typography>
      <div className={styles.price}>
        <ProductPrice />
        {isProProduct && <ProLogoIcon width={36} height={16} />}
      </div>

      <div className={styles.coupon}>
        <ProductCoupon
          context="A2C"
          productId={productId ?? 0}
          quantity={quantity}
          unitPrice={price?.primaryPrice.amountWithVat ?? 0}
          forceDisableTooltip
        />
      </div>
    </div>
  );
};
